import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Star, Package, ShoppingCart, PoundSterling, ExternalLink, MessageSquare, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { base44 } from '@/api/base44Client';

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-[#111115] border border-white/10 p-4 flex items-center gap-3">
      <span className="w-9 h-9 bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-primary" />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] text-white/40 uppercase tracking-wider">{label}</div>
        <div className="text-sm text-white font-medium truncate">{value}</div>
      </div>
    </div>
  );
}

export default function EbaySellerOverview({ settings }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await base44.functions.invoke('ebaySellerOverview', {});
      setData(res.data || null);
      if (res.data?.error) setError(res.data.error);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load eBay overview.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fmtMoney = (v) => '£' + Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const active = data?.active_listings;

  return (
    <div className="bg-[#111115] border border-white/10 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-white uppercase tracking-wider">Store Performance</h2>
          <p className="text-xs text-white/50 mt-1">
            Live eBay listings, feedback and sales pulled into Motion Reality.
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" className="bg-transparent border-white/10 text-white/70 hover:bg-white/5">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </Button>
      </div>

      {!settings?.seller_username && (
        <div className="mt-4 text-xs text-amber-400/90 bg-amber-500/5 border border-amber-500/20 p-3">
          Seller username isn't loaded yet — reconnect eBay once to grant the identity permission, then it will appear here.
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        </div>
      )}

      {error && (
        <div className="mt-4 text-xs text-red-400 bg-red-500/5 border border-red-500/20 p-3">{error}</div>
      )}

      {data && !data.error && (
        <div className="mt-5 space-y-6">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-white/40">Seller:</span>
              <span className="text-white font-medium">{data.seller_username || '—'}</span>
            </div>
            {data.feedback_profile_url && (
              <a href={data.feedback_profile_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                <MessageSquare className="w-3.5 h-3.5" /> View eBay feedback <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Active listings" value={active?.total ?? '—'} icon={Package} />
            <Stat label="Total watchers" value={active?.total_watchers ?? 0} icon={Eye} />
            <Stat label="Listings w/ watchers" value={active?.with_watchers ?? 0} icon={Eye} />
            <Stat label="Feedback" value={data.feedback_profile_url ? 'View profile' : '—'} icon={MessageSquare} />
            <Stat label="eBay orders pulled" value={data.our_stats.orders} icon={ShoppingCart} />
            <Stat label="eBay revenue" value={fmtMoney(data.our_stats.revenue)} icon={PoundSterling} />
            <Stat label="Items sold" value={data.our_stats.items_sold} icon={Star} />
            <Stat label="Inventory-API offers" value={data.offers.total} icon={Package} />
          </div>

          <div>
            <h3 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">
              Live Products for Sale ({active?.items?.length || 0}{active && active.total > (active.items?.length || 0) ? ` of ${active.total}` : ''})
            </h3>
            {!active || !active.items?.length ? (
              <p className="text-xs text-white/40">No active eBay listings found right now.</p>
            ) : (
              <div className="border border-white/10 overflow-hidden">
                <div className="max-h-[28rem] overflow-y-auto">
                  <table className="w-full text-xs table-fixed">
                    <thead className="bg-black/40 text-white/40 uppercase tracking-wider sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium w-[42%]">Product</th>
                        <th className="text-left px-3 py-2 font-medium w-[15%]">Item ID</th>
                        <th className="text-left px-3 py-2 font-medium w-[14%]">Type</th>
                        <th className="text-right px-3 py-2 font-medium w-[7%]">Qty</th>
                        <th className="text-right px-3 py-2 font-medium w-[8%]">Price</th>
                        <th className="text-right px-3 py-2 font-medium w-[8%]">Watchers</th>
                        <th className="text-right px-3 py-2 font-medium w-[6%]">Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {active.items.map((it) => (
                        <tr key={it.item_id} className="border-t border-white/5">
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {it.gallery_url ? (
                                <img src={it.gallery_url} alt="" className="w-8 h-8 object-cover border border-white/10 shrink-0" />
                              ) : (
                                <span className="w-8 h-8 bg-white/5 border border-white/10 shrink-0" />
                              )}
                              <span className="text-white/80 truncate">{it.title || '—'}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-white/50 font-mono truncate">{it.item_id || '—'}</td>
                          <td className="px-3 py-2 text-white/50 truncate">{it.listing_type || '—'}</td>
                          <td className="px-3 py-2 text-right text-white/70 whitespace-nowrap">{it.quantity ?? '—'}</td>
                          <td className="px-3 py-2 text-right text-white/70 whitespace-nowrap">{it.price != null ? `£${it.price}` : '—'}</td>
                          <td className="px-3 py-2 text-right text-white/70 whitespace-nowrap">{it.watch_count}</td>
                          <td className="px-3 py-2 text-right">
                            {it.view_url ? (
                              <a href={it.view_url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 whitespace-nowrap">
                                View <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}